// src/pages/onboarding-control/OnboardingControl.jsx

import React, { useState, useEffect, useCallback } from "react";
import { AnimatePresence } from "framer-motion";
import { packageApi } from "../../../api/packageApi";
import { websiteApi } from "../../../api/websiteApi";
import { toast } from "react-toastify";
import StepConfigCard from "./components/StepConfigCard";
import AddStepModal from "./components/AddStepModal";
import BusinessProfileQuestionsEditor from "./components/BusinessProfileQuestionsEditor";
import { DEFAULT_WEBSITE_ONBOARDING_QUESTIONS } from "../../client/StoreRequest/websiteOnboardingQuestions";
import "./OnboardingControl.css";

const OnboardingControl = () => {
  const [activeTab, setActiveTab] = useState("steps");
  const [steps, setSteps] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [websiteQuestions, setWebsiteQuestions] = useState(
    DEFAULT_WEBSITE_ONBOARDING_QUESTIONS,
  );

  // ============================================================
  // DOMAIN SETTINGS STATE
  // ============================================================
  const [domainSettings, setDomainSettings] = useState({
    enable_availability_check: "true",
    enable_dns_config: "false",
    domain_api_provider: "godaddy",
    godaddy_api_key: "",
    godaddy_api_secret: "",
    hostafrica_api_key: "",
    auto_configure_dns: "false",
  });

  // ============================================================
  // EXACT STEPS MATCHING RequestStore.jsx - DEFAULT STEPS
  // ============================================================
  const DEFAULT_STEPS = [
    {
      id: "package",
      name: "Package",
      title: "Choose Your Package",
      description: "Select the plan that best fits your business needs",
      enabled: true,
      required: true,
      order: 1,
      icon: "📦",
      color: "#4f46e5",
      tiers: [
        "starter",
        "lite",
        "growth",
        "pro",
        "enterprise",
        "premium",
        "business",
      ],
    },
    {
      id: "business",
      name: "Business",
      title: "Tell Us About Your Business",
      description: "Provide the details we need to set up your store",
      enabled: true,
      required: true,
      order: 2,
      icon: "🏪",
      color: "#7c3aed",
      tiers: [
        "starter",
        "lite",
        "growth",
        "pro",
        "enterprise",
        "premium",
        "business",
      ],
    },
    {
      id: "domain",
      name: "Domain",
      title: "Domain Setup",
      description: "Choose your domain or subdomain for your store",
      enabled: true,
      required: true,
      order: 3,
      icon: "🌐",
      color: "#059669",
      tiers: [
        "starter",
        "lite",
        "growth",
        "pro",
        "enterprise",
        "premium",
        "business",
      ],
    },
    {
      id: "design",
      name: "Design",
      title: "Store Design",
      description: "Customize the look and feel of your store",
      enabled: true,
      required: false,
      order: 4,
      icon: "🎨",
      color: "#d97706",
      tiers: ["growth", "pro", "enterprise", "premium", "business"],
    },
    {
      id: "products",
      name: "Products",
      title: "Products",
      description: "Add your products to start selling",
      enabled: true,
      required: false,
      order: 5,
      icon: "🏷️",
      color: "#dc2626",
      tiers: ["growth", "pro", "enterprise", "premium", "business"],
    },
    {
      id: "agreement",
      name: "Agreement",
      title: "Service Agreement",
      description: "Review and accept the service agreement",
      enabled: true,
      required: true,
      order: 6,
      icon: "📄",
      color: "#8b5cf6",
      tiers: [
        "starter",
        "lite",
        "growth",
        "pro",
        "enterprise",
        "premium",
        "business",
      ],
    },
    {
      id: "invoice",
      name: "Invoice",
      title: "Invoice & Payment",
      description: "Review invoice and payment details",
      enabled: true,
      required: true,
      order: 7,
      icon: "🧾",
      color: "#0891b2",
      tiers: [
        "starter",
        "lite",
        "growth",
        "pro",
        "enterprise",
        "premium",
        "business",
      ],
    },
  ];

  // Domain Extensions Control (Matches RequestStore) - WITH TAX INCLUDED
  const [domainExtensions, setDomainExtensions] = useState([
    { extension: ".co.za", price: 89.0, currency: "ZAR", popular: true },
    { extension: ".com", price: 129.99, currency: "ZAR", popular: true },
    { extension: ".org", price: 119.99, currency: "ZAR", popular: false },
    { extension: ".net", price: 119.99, currency: "ZAR", popular: false },
    { extension: ".shop", price: 99.99, currency: "ZAR", popular: false },
    { extension: ".store", price: 99.99, currency: "ZAR", popular: false },
    { extension: ".online", price: 89.99, currency: "ZAR", popular: false },
  ]);

  // Design Templates Control (Matches RequestStore)
  const [templates, setTemplates] = useState([
    {
      id: "minimal",
      name: "Minimal",
      description: "Clean & simple",
      icon: "🎯",
    },
    {
      id: "modern",
      name: "Modern",
      description: "Contemporary & sleek",
      icon: "✨",
    },
    {
      id: "classic",
      name: "Classic",
      description: "Timeless & elegant",
      icon: "🏛️",
    },
    {
      id: "vibrant",
      name: "Vibrant",
      description: "Bold & energetic",
      icon: "🌈",
    },
  ]);

  // Service Agreement Sections
  const [agreementSections, setAgreementSections] = useState([
    {
      title: "1. Services Provided",
      content:
        "Toora Flex agrees to design, configure, deploy, and manage one (1) professionally branded online store on the Toora Flex eCommerce platform.",
    },
    {
      title: "2. Fees & Payment",
      content:
        "Domain registration, store plan setup, and monthly platform maintenance fees as selected.",
    },
    {
      title: "3. Timeline",
      content:
        "Standard deployment is completed within 7 Business Days from receipt of signed agreement, initial payment, and all mandatory client assets.",
    },
  ]);
  const PACKAGE_TIERS = [
    { id: "starter", label: "Starter", color: "#6b7280" },
    { id: "lite", label: "Lite", color: "#3b82f6" },
    { id: "growth", label: "Growth", color: "#10b981" },
    { id: "pro", label: "Pro", color: "#8b5cf6" },
    { id: "enterprise", label: "Enterprise", color: "#ef4444" },
    { id: "premium", label: "Premium", color: "#f59e0b" },
    { id: "business", label: "Business", color: "#06b6d4" },
  ];

  // Helper function to extract features like RequestStore
  const getFeatureEntries = (features) => {
    if (!features) return [];
    let featuresObj = features;
    if (typeof features === "string") {
      try {
        featuresObj = JSON.parse(features);
      } catch (e) {
        return [];
      }
    }
    if (typeof featuresObj !== "object" || Array.isArray(featuresObj))
      return [];
    return Object.entries(featuresObj)
      .filter(([key, value]) => value && String(value).trim() !== "")
      .map(([key, value]) => ({ key, value: String(value).trim() }));
  };

  // Fetch Domain Settings
  const fetchDomainSettings = useCallback(async () => {
    try {
      const response = await fetch("/api/v1/admin/onboarding/domain-settings", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const data = await response.json();
      if (data && data.success && data.settings) {
        setDomainSettings(data.settings);
        console.log("✅ Domain settings loaded:", data.settings);
      }
    } catch (error) {
      console.error("Error fetching domain settings:", error);
    }
  }, []);

  // Fetch Packages (Using same packageApi as RequestStore)
  const fetchPackages = useCallback(async () => {
    try {
      const response = await packageApi.getPackages(true);
      let packagesData = [];
      if (response && response.success) {
        packagesData = response.data || [];
      } else if (Array.isArray(response)) {
        packagesData = response;
      }
      setPackages(packagesData.filter((pkg) => pkg.IsActive !== false));
    } catch (err) {
      console.error("Error fetching packages:", err);
    }
  }, []);

  // Fetch Onboarding Configuration - ADMIN ENDPOINT
  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      await fetchPackages();
      await fetchDomainSettings();

      const response = await fetch("/api/v1/admin/onboarding/config", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Full API Response:", data);

      if (data && data.success && data.config) {
        if (data.config.steps && data.config.steps.length > 0) {
          const formattedSteps = data.config.steps.map((step) => ({
            id: step.id || step.StepKey,
            name: step.name || "",
            title: step.title || step.name || "",
            description: step.description || "",
            enabled:
              step.id === "domain" || step.StepKey === "domain"
                ? true
                : step.enabled !== undefined
                  ? step.enabled
                  : true,
            required:
              step.id === "domain" || step.StepKey === "domain"
                ? true
                : step.required !== undefined
                  ? step.required
                  : true,
            order: step.order || 0,
            icon: step.icon || "📌",
            color: step.color || "#4f46e5",
            tiers: step.tiers || [],
          }));
          if (formattedSteps.some((step) => !step.id)) {
            throw new Error(
              "Onboarding steps are missing database keys. Run the onboarding database migration and try again.",
            );
          }
          setSteps(formattedSteps.sort((a, b) => a.order - b.order));
        } else {
          throw new Error(
            "No onboarding steps were returned by the database. Run the onboarding database migration and try again.",
          );
        }

        if (Array.isArray(data.config.domainExtensions)) {
          setDomainExtensions(data.config.domainExtensions);
        }

        if (data.config.templates && data.config.templates.length > 0) {
          setTemplates(data.config.templates);
        }

        if (
          data.config.agreementSections &&
          data.config.agreementSections.length > 0
        ) {
          setAgreementSections(data.config.agreementSections);
        }
        setConfigLoaded(true);
        return true;
      } else {
        throw new Error(
          data?.message || "The database did not return onboarding configuration.",
        );
      }
    } catch (err) {
      console.error("Error fetching onboarding config:", err);
      setConfigLoaded(false);
      setSteps([]);
      setError(err.message || "Failed to load onboarding configuration.");
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchPackages, fetchDomainSettings]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    websiteApi
      .getOnboardingQuestions()
      .then((response) => {
        if (response.data) setWebsiteQuestions(response.data);
      })
      .catch((loadError) => {
        setError(
          loadError.response?.data?.error ||
            "Failed to load website onboarding questions.",
        );
      });
  }, []);

  const saveWebsiteQuestions = async () => {
    try {
      setSaving(true);
      setError("");
      await websiteApi.saveOnboardingQuestions(websiteQuestions);
      toast.success("Website onboarding questions saved.");
    } catch (saveError) {
      setError(
        saveError.response?.data?.error ||
          "Failed to save website onboarding questions.",
      );
    } finally {
      setSaving(false);
    }
  };

  // Save Domain Settings
  const saveDomainSettings = async () => {
    try {
      setSaving(true);
      setError("");

      const response = await fetch("/api/v1/admin/onboarding/domain-settings", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(domainSettings),
      });

      const data = await response.json();

      if (data && data.success) {
        toast.success("Domain settings updated successfully!");
      } else {
        setError(data?.message || "Failed to save domain settings");
      }
    } catch (error) {
      console.error("Error saving domain settings:", error);
      setError("An error occurred while saving domain settings");
    } finally {
      setSaving(false);
    }
  };

  // Save Configuration - ADMIN ENDPOINT
  const saveConfig = async () => {
    try {
      setSaving(true);
      setError("");

      const payload = {
        steps: steps.map((step) =>
          step.id === "domain"
            ? { ...step, enabled: true, required: true }
            : step,
        ),
        domainExtensions,
        templates,
        agreementSections,
      };

      const response = await fetch("/api/v1/admin/onboarding/config", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data && data.success) {
        if (await fetchConfig()) {
          toast.success(
            "RequestStore onboarding controls updated successfully!",
          );
        } else {
          setError(
            "Configuration was saved, but the saved version could not be reloaded.",
          );
        }
      } else {
        setError(data?.message || "Failed to save onboarding configuration.");
      }
    } catch (err) {
      console.error("Error saving config:", err);
      setError("An error occurred while saving onboarding controls.");
    } finally {
      setSaving(false);
    }
  };

  // Reset to Default - Uses DEFAULT_STEPS constant AND saves to database
  const resetToDefault = async () => {
    if (window.confirm("Reset all controls to RequestStore defaults?")) {
      try {
        setSaving(true);

        // Reset UI state to DEFAULT_STEPS
        setSteps(DEFAULT_STEPS);

        // Reset domain extensions
        const defaultDomains = [
          { extension: ".co.za", price: 89.0, currency: "ZAR", popular: true },
          { extension: ".com", price: 129.99, currency: "ZAR", popular: true },
          { extension: ".org", price: 119.99, currency: "ZAR", popular: false },
          { extension: ".net", price: 119.99, currency: "ZAR", popular: false },
          { extension: ".shop", price: 99.99, currency: "ZAR", popular: false },
          {
            extension: ".store",
            price: 99.99,
            currency: "ZAR",
            popular: false,
          },
          {
            extension: ".online",
            price: 89.99,
            currency: "ZAR",
            popular: false,
          },
        ];
        setDomainExtensions(defaultDomains);

        // Reset templates
        const defaultTemplates = [
          {
            id: "minimal",
            name: "Minimal",
            description: "Clean & simple",
            icon: "🎯",
          },
          {
            id: "modern",
            name: "Modern",
            description: "Contemporary & sleek",
            icon: "✨",
          },
          {
            id: "classic",
            name: "Classic",
            description: "Timeless & elegant",
            icon: "🏛️",
          },
          {
            id: "vibrant",
            name: "Vibrant",
            description: "Bold & energetic",
            icon: "🌈",
          },
        ];
        setTemplates(defaultTemplates);

        // Reset agreement sections
        const defaultAgreement = [
          {
            title: "1. Services Provided",
            content:
              "Toora Flex agrees to design, configure, deploy, and manage one (1) professionally branded online store on the Toora Flex eCommerce platform.",
          },
          {
            title: "2. Fees & Payment",
            content:
              "Domain registration, store plan setup, and monthly platform maintenance fees as selected.",
          },
          {
            title: "3. Timeline",
            content:
              "Standard deployment is completed within 7 Business Days from receipt of signed agreement, initial payment, and all mandatory client assets.",
          },
        ];
        setAgreementSections(defaultAgreement);

        // Reset domain settings
        const defaultDomainSettings = {
          enable_availability_check: "true",
          enable_dns_config: "false",
          domain_api_provider: "godaddy",
          godaddy_api_key: "",
          godaddy_api_secret: "",
          hostafrica_api_key: "",
          auto_configure_dns: "false",
        };
        setDomainSettings(defaultDomainSettings);

        // Save to database
        const payload = {
          steps: DEFAULT_STEPS,
          domainExtensions: defaultDomains,
          templates: defaultTemplates,
          agreementSections: defaultAgreement,
        };

        // Save config
        const configResponse = await fetch("/api/v1/admin/onboarding/config", {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(payload),
        });

        const configData = await configResponse.json();

        // Save domain settings
        const domainResponse = await fetch(
          "/api/v1/admin/onboarding/domain-settings",
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            body: JSON.stringify(defaultDomainSettings),
          },
        );

        const domainData = await domainResponse.json();

        if (
          configData &&
          configData.success &&
          domainData &&
          domainData.success
        ) {
          if (await fetchConfig()) {
            toast.success(
              "All controls reset to default values and saved to database!",
            );
          } else {
            setError(
              "Defaults were saved, but the saved configuration could not be reloaded.",
            );
          }
        } else {
          setError("Failed to save reset configuration to database.");
        }

        setTimeout(() => setError(""), 3000);
      } catch (err) {
        console.error("Error resetting config:", err);
        setError("An error occurred while resetting onboarding controls.");
      } finally {
        setSaving(false);
      }
    }
  };

  // Step Controllers
  const toggleEnabled = (stepId) => {
    if (stepId === "domain") return;
    setSteps((prev) =>
      prev.map((step) =>
        step.id === stepId ? { ...step, enabled: !step.enabled } : step,
      ),
    );
  };

  const toggleRequired = (stepId) => {
    if (stepId === "domain") return;
    setSteps((prev) =>
      prev.map((step) =>
        step.id === stepId ? { ...step, required: !step.required } : step,
      ),
    );
  };

  const moveStep = (stepId, direction) => {
    const index = steps.findIndex((s) => s.id === stepId);
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === steps.length - 1)
    ) {
      return;
    }

    const newSteps = [...steps];
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    const temp = newSteps[index];
    newSteps[index] = newSteps[swapIndex];
    newSteps[swapIndex] = temp;

    setSteps(newSteps.map((step, idx) => ({ ...step, order: idx + 1 })));
  };

  const updateStep = (stepId, updates) => {
    setSteps((prev) =>
      prev.map((step) => (step.id === stepId ? { ...step, ...updates } : step)),
    );
  };

  const addStep = (newStep) => {
    const maxOrder = steps.reduce((max, s) => Math.max(max, s.order || 0), 0);
    setSteps([
      ...steps,
      {
        ...newStep,
        order: maxOrder + 1,
        enabled: true,
        required: false,
        tiers: [
          "starter",
          "lite",
          "growth",
          "pro",
          "enterprise",
          "premium",
          "business",
        ],
      },
    ]);
    setShowAddModal(false);
  };

  const deleteStep = (stepId) => {
    if (window.confirm("Delete this step from RequestStore onboarding flow?")) {
      setSteps((prev) => {
        const filtered = prev.filter((s) => s.id !== stepId);
        return filtered.map((step, idx) => ({ ...step, order: idx + 1 }));
      });
    }
  };

  if (loading) {
    return (
      <div className="onboarding-control-loading">
        <div className="spinner"></div>
        <p>Loading RequestStore Controls...</p>
      </div>
    );
  }

  return (
    <div className="onboarding-control-page">
      {/* Page Header */}
      <div className="onboarding-control-header">
        <div className="header-left">
          <h1>Onboarding Control Center</h1>
          <p className="subtitle">
            Manage ecommerce onboarding and website project questions.
          </p>
        </div>
        <div className="header-right">
          <button
            className="btn-reset"
            onClick={resetToDefault}
            disabled={saving || !configLoaded}
          >
            <i className="fas fa-undo"></i> Reset Defaults
          </button>
          <button
            className="btn-add"
            onClick={() => setShowAddModal(true)}
            disabled={!configLoaded}
          >
            <i className="fas fa-plus"></i> Add Step
          </button>
          <button
            className="btn-save"
            onClick={saveConfig}
            disabled={saving || !configLoaded}
          >
            {saving ? "Saving..." : "Save Controls"}
          </button>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="control-navigation-tabs">
        <button
          className={`tab-btn ${activeTab === "steps" ? "active" : ""}`}
          onClick={() => setActiveTab("steps")}
        >
          ⚙️ Wizard Steps ({steps.length})
        </button>
        <button
          className={`tab-btn ${activeTab === "packages" ? "active" : ""}`}
          onClick={() => setActiveTab("packages")}
        >
          📦 Active Packages ({packages.length})
        </button>
        <button
          className={`tab-btn ${activeTab === "websiteQuestions" ? "active" : ""}`}
          onClick={() => setActiveTab("websiteQuestions")}
        >
          🌐 Website Questions
        </button>
        <button
          className={`tab-btn ${activeTab === "domains" ? "active" : ""}`}
          onClick={() => setActiveTab("domains")}
        >
          🌐 Domain TLD Pricing ({domainExtensions.length})
        </button>
        <button
          className={`tab-btn ${activeTab === "templates" ? "active" : ""}`}
          onClick={() => setActiveTab("templates")}
        >
          🎨 Design Templates ({templates.length})
        </button>
        <button
          className={`tab-btn ${activeTab === "domainsettings" ? "active" : ""}`}
          onClick={() => setActiveTab("domainsettings")}
        >
          🌐 Domain Settings
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {/* TAB 1: STEPS CONTROL */}
      {activeTab === "steps" && (
        <>
          {/* Live Progress Bar Preview */}
          <div className="request-store-indicator-preview">
            <h4>Live Wizard Progress Indicator Preview</h4>
            <div className="step-dots-preview">
              {steps.map((step, idx) => (
                <div
                  key={step.id}
                  className={`preview-dot ${step.enabled ? "active" : "disabled"}`}
                >
                  <span
                    className="dot-num"
                    style={{
                      background: step.enabled ? step.color : "#d1d5db",
                    }}
                  >
                    {step.icon || idx + 1}
                  </span>
                  <span className="dot-label">{step.name}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="steps-list">
            <div className="steps-header">
              <span className="col-order">Order</span>
              <span className="col-status">Status</span>
              <span className="col-step">Step Name & Title</span>
              <span className="col-tiers">Available Package Tiers</span>
              <span className="col-required">Required</span>
              <span className="col-actions">Actions</span>
            </div>

            <AnimatePresence>
              {steps.map((step, index) => (
                <StepConfigCard
                  key={step.id || `step-${index}`}
                  step={step}
                  index={index}
                  totalSteps={steps.length}
                  onToggleEnabled={toggleEnabled}
                  onToggleRequired={toggleRequired}
                  onMove={moveStep}
                  onUpdate={updateStep}
                  onDelete={deleteStep}
                  packageTiers={PACKAGE_TIERS}
                />
              ))}
            </AnimatePresence>
          </div>
        </>
      )}

      {/* TAB 2: PACKAGES DISPLAY */}
      {activeTab === "packages" && (
        <div className="control-panel-section">
          <h3>Store Request Packages View</h3>
          <p className="section-desc">
            Fetched active plans via <code>packageApi.getPackages(true)</code>.
            Showing exact card layout as rendered in Step 1.
          </p>
          <div className="package-grid-compact" style={{ marginTop: "16px" }}>
            {packages.map((pkg) => {
              const isPopular = String(pkg.Tier).toLowerCase() === "growth";
              const featureEntries = getFeatureEntries(pkg.Features);
              const tier = String(pkg.Tier).toLowerCase();
              const hasFreeDomain = [
                "growth",
                "pro",
                "enterprise",
                "premium",
                "business",
              ].includes(tier);

              return (
                <div
                  key={pkg.PackageID}
                  className={`package-card-compact ${isPopular ? "popular" : ""}`}
                >
                  {isPopular && (
                    <div className="popular-badge-compact">POPULAR</div>
                  )}

                  <div className="package-card-header-compact">
                    <h4>{pkg.Name}</h4>
                    <span className="package-tier-compact">{pkg.Tier}</span>
                  </div>
                  <div className="package-price-compact">
                    R{Number(pkg.Price).toLocaleString()}
                    <span>/{pkg.BillingPeriod || "month"}</span>
                  </div>
                  {hasFreeDomain && (
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#10b981",
                        fontWeight: "600",
                        marginBottom: "4px",
                      }}
                    >
                      ✓ Free Domain Included
                    </div>
                  )}
                  {pkg.SetupFee > 0 && (
                    <div className="package-setup-fee-compact">
                      <i className="fas fa-wallet"></i>
                      Setup: R{Number(pkg.SetupFee).toLocaleString()}
                    </div>
                  )}
                  <ul className="package-features-compact">
                    <li>
                      <i className="fas fa-check"></i>
                      <span>
                        {pkg.ProductLimit
                          ? `${pkg.ProductLimit} products`
                          : "Unlimited"}
                      </span>
                    </li>
                    {featureEntries.slice(0, 3).map(({ key, value }) => (
                      <li key={key}>
                        <i className="fas fa-check"></i>
                        <span>{value}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {activeTab === "websiteQuestions" && (
        <div className="control-panel-section">
          <BusinessProfileQuestionsEditor
            sections={websiteQuestions}
            onChange={setWebsiteQuestions}
            eyebrow="WEBSITE CLIENT ONBOARDING"
            title="Website project questions"
            description="Configure the questions website clients answer after registration. These are separate from ecommerce business-readiness questions."
            questionLimit={30}
          />
          <button
            className="btn-save"
            onClick={saveWebsiteQuestions}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Website Questions"}
          </button>
        </div>
      )}

      {/* TAB 3: DOMAINS CONTROL */}
      {activeTab === "domains" && (
        <div className="control-panel-section">
          <h3>Domain Extensions & Pricing Controls</h3>
          <p className="section-desc">
            Configure default TLD prices shown to clients in Step 3 (Domain
            Setup). Enter prices before VAT; clients see the price with 15% VAT
            included.
          </p>
          <div className="domain-controls-grid">
            {domainExtensions.map((ext, idx) => (
              <div key={ext.extension} className="domain-card-control">
                <div className="ext-badge">{ext.extension}</div>
                <div className="form-group-inline">
                  <label>Price before VAT:</label>
                  <input
                    type="number"
                    value={ext.price}
                    onChange={(e) => {
                      const price = parseFloat(e.target.value);
                      setDomainExtensions((previous) =>
                        previous.map((item, itemIndex) =>
                          itemIndex === idx
                            ? { ...item, price: Number.isNaN(price) ? 0 : price }
                            : item,
                        ),
                      );
                    }}
                    step="0.01"
                    min="0"
                  />
                </div>
                <p>
                  Client price incl. 15% VAT: R
                  {(
                    Math.round(
                      (Number(ext.price || 0) * 1.15 + Number.EPSILON) * 100,
                    ) / 100
                  ).toFixed(2)}
                </p>
                <label className="checkbox-control">
                  <input
                    type="checkbox"
                    checked={Boolean(ext.popular)}
                    onChange={() =>
                      setDomainExtensions((previous) =>
                        previous.map((item, itemIndex) =>
                          itemIndex === idx
                            ? { ...item, popular: !item.popular }
                            : item,
                        ),
                      )
                    }
                  />
                  <span>Mark as ⭐ Popular</span>
                </label>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 4: TEMPLATES CONTROL */}
      {activeTab === "templates" && (
        <div className="templates-section-container">
          <div className="templates-header">
            <h3>🎨 Store Design Templates & Live Previews</h3>
            <p>
              Manage themes, descriptions, color palettes, and preview settings
              for Step 4 of the store onboarding flow.
            </p>
          </div>

          <div className="templates-controls-grid">
            {templates.map((tmpl, idx) => (
              <div key={tmpl.id} className="template-card-control">
                <div className="template-card-header">
                  <input
                    type="text"
                    value={tmpl.icon}
                    onChange={(e) => {
                      const updated = [...templates];
                      updated[idx].icon = e.target.value;
                      setTemplates(updated);
                    }}
                    className="icon-input"
                    title="Template Icon Emoji"
                  />
                  <input
                    type="text"
                    value={tmpl.name}
                    onChange={(e) => {
                      const updated = [...templates];
                      updated[idx].name = e.target.value;
                      setTemplates(updated);
                    }}
                    className="name-input"
                    placeholder="Template Name"
                  />
                </div>

                <textarea
                  value={tmpl.description}
                  onChange={(e) => {
                    const updated = [...templates];
                    updated[idx].description = e.target.value;
                    setTemplates(updated);
                  }}
                  rows="2"
                  placeholder="Template description..."
                />

                <div className={`template-preview-box preview-${tmpl.id}`}>
                  <div className="preview-navbar">
                    <div className="preview-logo">
                      <span>{tmpl.icon}</span> Store
                    </div>
                    <div className="preview-nav-links">
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                  <div className="preview-hero">
                    <div className="preview-hero-title"></div>
                    <div className="preview-hero-btn"></div>
                  </div>
                  <div className="preview-grid">
                    <div className="preview-item"></div>
                    <div className="preview-item"></div>
                    <div className="preview-item"></div>
                  </div>
                  <div className="preview-overlay">
                    <button
                      type="button"
                      className="btn-preview-modal"
                      onClick={() =>
                        alert(`Opening live store preview for ${tmpl.name}`)
                      }
                    >
                      <i className="fas fa-eye"></i> Full Preview
                    </button>
                  </div>
                </div>

                <div className="template-card-footer">
                  <span className="template-id-tag">id: {tmpl.id}</span>
                  <span className="preview-status">
                    ✓ Ready for Store Setup
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 6: DOMAIN SETTINGS */}
      {activeTab === "domainsettings" && (
        <div className="control-panel-section">
          <h3>🌐 Domain Configuration Settings</h3>
          <p className="section-desc">
            Configure domain availability checking and DNS automation settings.
            These settings control the behavior of the Domain Setup step in the
            client onboarding flow.
          </p>

          <div className="domain-settings-grid">
            {/* Availability Check */}
            <div className="setting-card">
              <h4>🔍 Availability Check</h4>
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={domainSettings.enable_availability_check === "true"}
                  onChange={(e) =>
                    setDomainSettings({
                      ...domainSettings,
                      enable_availability_check: e.target.checked
                        ? "true"
                        : "false",
                    })
                  }
                />
                <span>Enable Domain Availability Check</span>
              </label>
              <small>
                When disabled, users can enter any domain without validation.
                This bypasses GoDaddy/HostAfrica API calls.
              </small>
            </div>

            {/* DNS Configuration */}
            <div className="setting-card">
              <h4>⚙️ DNS Configuration</h4>
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={domainSettings.enable_dns_config === "true"}
                  onChange={(e) =>
                    setDomainSettings({
                      ...domainSettings,
                      enable_dns_config: e.target.checked ? "true" : "false",
                    })
                  }
                />
                <span>Enable DNS Configuration</span>
              </label>
              <small>
                Enable automatic DNS configuration for registered domains.
              </small>
            </div>

            {/* Auto Configure DNS */}
            <div className="setting-card">
              <h4>🔄 Auto-Configure DNS</h4>
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={domainSettings.auto_configure_dns === "true"}
                  onChange={(e) =>
                    setDomainSettings({
                      ...domainSettings,
                      auto_configure_dns: e.target.checked ? "true" : "false",
                    })
                  }
                  disabled={domainSettings.enable_dns_config !== "true"}
                />
                <span>Auto-Configure DNS Records</span>
              </label>
              <small>
                Automatically set up A, CNAME, and MX records for the domain.
                Only available when DNS Configuration is enabled.
              </small>
            </div>

            {/* API Provider */}
            <div className="setting-card">
              <h4>🏢 API Provider</h4>
              <select
                value={domainSettings.domain_api_provider || "godaddy"}
                onChange={(e) =>
                  setDomainSettings({
                    ...domainSettings,
                    domain_api_provider: e.target.value,
                  })
                }
              >
                <option value="godaddy">GoDaddy</option>
                <option value="hostafrica">HostAfrica</option>
                <option value="none">None (Simulation Mode)</option>
              </select>
              <small>
                Select the domain API provider for availability checks.
              </small>
            </div>

            {/* GoDaddy API Keys */}
            {domainSettings.domain_api_provider === "godaddy" && (
              <>
                <div className="setting-card">
                  <h4>🔑 GoDaddy API Key</h4>
                  <input
                    type="text"
                    value={domainSettings.godaddy_api_key || ""}
                    onChange={(e) =>
                      setDomainSettings({
                        ...domainSettings,
                        godaddy_api_key: e.target.value,
                      })
                    }
                    placeholder="Enter GoDaddy API Key"
                  />
                  <small>Get your API key from GoDaddy Developer Portal.</small>
                </div>
                <div className="setting-card">
                  <h4>🔐 GoDaddy API Secret</h4>
                  <input
                    type="password"
                    value={domainSettings.godaddy_api_secret || ""}
                    onChange={(e) =>
                      setDomainSettings({
                        ...domainSettings,
                        godaddy_api_secret: e.target.value,
                      })
                    }
                    placeholder="Enter GoDaddy API Secret"
                  />
                  <small>
                    Keep this secure. It will not be displayed again.
                  </small>
                </div>
              </>
            )}

            {/* HostAfrica API Key */}
            {domainSettings.domain_api_provider === "hostafrica" && (
              <div className="setting-card">
                <h4>🔑 HostAfrica API Key</h4>
                <input
                  type="text"
                  value={domainSettings.hostafrica_api_key || ""}
                  onChange={(e) =>
                    setDomainSettings({
                      ...domainSettings,
                      hostafrica_api_key: e.target.value,
                    })
                  }
                  placeholder="Enter HostAfrica API Key"
                />
                <small>Get your API key from HostAfrica control panel.</small>
              </div>
            )}

            {/* Summary Card */}
            <div className="setting-card summary-card">
              <h4>📊 Current Status</h4>
              <div className="status-summary">
                <div className="status-item">
                  <span className="status-label">Availability Check:</span>
                  <span
                    className={`status-value ${domainSettings.enable_availability_check === "true" ? "enabled" : "disabled"}`}
                  >
                    {domainSettings.enable_availability_check === "true"
                      ? "✅ Enabled"
                      : "❌ Disabled"}
                  </span>
                </div>
                <div className="status-item">
                  <span className="status-label">DNS Configuration:</span>
                  <span
                    className={`status-value ${domainSettings.enable_dns_config === "true" ? "enabled" : "disabled"}`}
                  >
                    {domainSettings.enable_dns_config === "true"
                      ? "✅ Enabled"
                      : "❌ Disabled"}
                  </span>
                </div>
                <div className="status-item">
                  <span className="status-label">API Provider:</span>
                  <span className="status-value">
                    {domainSettings.domain_api_provider || "None"}
                  </span>
                </div>
                <div className="status-item">
                  <span className="status-label">Auto-DNS:</span>
                  <span
                    className={`status-value ${domainSettings.auto_configure_dns === "true" ? "enabled" : "disabled"}`}
                  >
                    {domainSettings.auto_configure_dns === "true"
                      ? "✅ Enabled"
                      : "❌ Disabled"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="domain-settings-actions">
            <button
              className="btn-save-domain"
              onClick={saveDomainSettings}
              disabled={saving}
            >
              {saving ? "Saving..." : "💾 Save Domain Settings"}
            </button>
          </div>
        </div>
      )}

      {/* Add Step Modal */}
      <AddStepModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={addStep}
        existingIds={steps.map((s) => s.id)}
        packageTiers={PACKAGE_TIERS}
      />
    </div>
  );
};

export default OnboardingControl;
